import { useCallback, useEffect, useState } from 'react';
import { Inbox, Loader2, Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { newsletterDraftsApi } from '../../../api/newsletterDraftsApi';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

interface DraftRow {
  id: number;
  subscriber_email?: string;
  subject?: string;
  status: string;
  created_at?: string;
}

function formatDate(iso: string | undefined, localeTag: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(localeTag, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function DraftsOutboxPage() {
  const { t, localeTag } = useUiLocale();
  const [pendingDrafts, setPendingDrafts] = useState<DraftRow[]>([]);
  const [allDrafts, setAllDrafts] = useState<DraftRow[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingAll, setLoadingAll] = useState(true);

  /* ── Drawer state ── */
  const [drawerDraftId, setDrawerDraftId] = useState<number | null>(null);
  const [drawerHtml, setDrawerHtml] = useState<string | null>(null);
  const [loadingDrawer, setLoadingDrawer] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<number | null>(null);
  const [acting, setActing] = useState(false);

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await newsletterDraftsApi.listPending(undefined);
      const rows = Array.isArray(res.data) ? res.data : [];
      setPendingDrafts(rows.map((r: any) => ({
        id: r.id,
        subscriber_email: r.subscriber_email ?? r.subscriber?.email ?? '—',
        subject: r.subject ?? '—',
        status: r.status ?? 'pending_review',
        created_at: r.created_at,
      })));
    } catch {
      toast.error(t('draftsOutbox.toastPendingFailed'));
    } finally {
      setLoadingPending(false);
    }
  }, [t]);

  const loadAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await newsletterDraftsApi.list({ limit: 50 });
      const rows = Array.isArray(res.data) ? res.data : [];
      setAllDrafts(rows.map((r: any) => ({
        id: r.id,
        subscriber_email: r.subscriber_email ?? r.subscriber?.email ?? '—',
        subject: r.subject ?? '—',
        status: r.status ?? '—',
        created_at: r.created_at,
      })));
    } catch {
      toast.error(t('draftsOutbox.toastLoadFailed'));
    } finally {
      setLoadingAll(false);
    }
  }, [t]);

  useEffect(() => {
    loadPending();
    loadAll();
  }, [loadPending, loadAll]);

  const openDrawer = async (id: number) => {
    setDrawerDraftId(id);
    setDrawerHtml(null);
    setLoadingDrawer(true);
    try {
      const res = await newsletterDraftsApi.get(id, undefined);
      const d = res.data as any;
      const html = d?.html_body ?? d?.body ?? d?.html ?? '';
      setDrawerHtml(html);
    } catch {
      setDrawerHtml(`<p>${t('draftsOutbox.loadFailed')}</p>`);
    } finally {
      setLoadingDrawer(false);
    }
  };

  const closeDrawer = () => { setDrawerDraftId(null); setDrawerHtml(null); };

  const handleSend = async (id: number) => {
    setActing(true);
    try {
      await newsletterDraftsApi.send(id, undefined);
      toast.success(t('draftsOutbox.toastSent'));
      closeDrawer();
      await Promise.all([loadPending(), loadAll()]);
    } catch {
      toast.error(t('draftsOutbox.toastSendFailed'));
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (rejectTarget == null) return;
    setActing(true);
    try {
      await newsletterDraftsApi.reject(rejectTarget, undefined);
      toast.success(t('draftsOutbox.toastRejected'));
      setRejectTarget(null);
      closeDrawer();
      await Promise.all([loadPending(), loadAll()]);
    } catch {
      toast.error(t('draftsOutbox.toastRejectFailed'));
    } finally {
      setActing(false);
    }
  };

  function DraftTable({ drafts, loading }: { drafts: DraftRow[]; loading: boolean }) {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      );
    }
    if (drafts.length === 0) {
      return <p className="py-10 text-center text-sm text-slate-500">{t('draftsOutbox.empty')}</p>;
    }
    return (
      <table className="w-full text-sm">
        <thead>
          <tr className="hawk-table-head-row text-left">
            <th className="px-4 py-3">{t('common.subscriber')}</th>
            <th className="px-4 py-3">{t('draftsOutbox.colSubject')}</th>
            <th className="px-4 py-3">{t('common.status')}</th>
            <th className="px-4 py-3">{t('draftsOutbox.colCreated')}</th>
            <th className="w-20 px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {drafts.map((d, i) => (
            <tr
              key={d.id}
              className={`cursor-pointer transition-colors hover:bg-slate-50 ${i > 0 ? 'border-t border-slate-200/70' : ''}`}
              onClick={() => openDrawer(d.id)}
            >
              <td className="px-4 py-3 font-medium text-slate-800">{d.subscriber_email}</td>
              <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{d.subject}</td>
              <td className="px-4 py-3">
                <Badge
                  variant="outline"
                  className={`hawk-outside-curve-pill font-medium capitalize text-xs ${
                    d.status === 'pending_review'
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : d.status === 'sent'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  {d.status.replace('_', ' ')}
                </Badge>
              </td>
              <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(d.created_at, localeTag)}</td>
              <td className="px-4 py-3">
                <Mail className="h-3.5 w-3.5 text-slate-400" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="hawk-admin-surface flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="hawk-glass-header hawk-glass-header--login shrink-0">
        <div className="px-1 py-2 sm:py-2.5">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            <span className="hawk-gradient-text">{t('draftsOutbox.title')}</span>
          </h1>
          <p className="hawk-page-subtitle mt-0.5 text-sm leading-tight">{t('draftsOutbox.subtitle')}</p>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="mb-3">
            <TabsTrigger value="pending" className="gap-1.5">
              <Inbox className="h-3.5 w-3.5" />
              {t('draftsOutbox.tabPending')}
              {pendingDrafts.length > 0 && (
                <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                  {pendingDrafts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="all">{t('draftsOutbox.tabAll')}</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card className="border-white/10 shadow-none">
              <CardContent className="p-0">
                <DraftTable drafts={pendingDrafts} loading={loadingPending} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all">
            <Card className="border-white/10 shadow-none">
              <CardContent className="p-0">
                <DraftTable drafts={allDrafts} loading={loadingAll} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Drawer ── */}
      {drawerDraftId != null && (
        <div className="fixed inset-0 z-50 flex">
          <button
            type="button"
            className="flex-1 bg-black/30"
            onClick={closeDrawer}
            aria-label={t('common.close')}
          />
          <div className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <p className="font-semibold text-slate-800">{t('draftsOutbox.draftTitle', { id: drawerDraftId })}</p>
              <button type="button" onClick={closeDrawer} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-2">
              {loadingDrawer ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <iframe
                  srcDoc={drawerHtml ?? ''}
                  title={t('draftsOutbox.previewTitle')}
                  className="h-full w-full rounded border-0"
                  sandbox="allow-same-origin"
                />
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-slate-200 px-5 py-4">
              <Button
                type="button"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setRejectTarget(drawerDraftId)}
                disabled={acting}
              >
                {t('common.reject')}
              </Button>
              <Button
                type="button"
                className="ml-auto gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleSend(drawerDraftId)}
                disabled={acting}
              >
                {acting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                {t('common.send')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject confirm */}
      <AlertDialog open={rejectTarget != null} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('draftsOutbox.rejectConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('draftsOutbox.rejectDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleReject}
              disabled={acting}
            >
              {t('common.reject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
